/**
 * Profile card domain (chat-centric revamp T24), frontend mirror of
 * backend/src/services/chat_agent/profile_card.py.
 *
 * Same doctrine as thesisCard.ts: the card is not stored client-side
 * (transcript-as-state) — it is a projection of chat messages' `metadata`.
 * Tolerant coercion (clamp + drop, never throw) and the replay rule are kept
 * semantics-identical to the backend so both sides project the same card from
 * the same transcript. Edit folding mirrors the backend (applyProfileEdit +
 * replayProfileSession), added 2026-10-31.
 */
import type { ChatMessage } from './chatApi';
import type { ProfileCard } from './chatStream';

export const RISK_LEVELS = ['Conservative', 'Moderate', 'Balanced', 'Growth'] as const;
export const KNOWLEDGE_LEVELS = ['beginner', 'intermediate', 'advanced', 'expert'] as const;
export const TURNOVER_TOLERANCES = ['very low', 'low', 'medium', 'high'] as const;

const NAME_MAX = 120;
const TURNOVER_MAX = 40;
const DESC_MAX = 1000;
const PROGRESS_FIELDS = 3; // style_name, target_risk_level, knowledge_level

function isDict(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function cleanStr(raw: unknown, cap: number): string | null {
  if (typeof raw !== 'string') return null;
  const s = raw.trim().slice(0, cap);
  return s || null;
}

/** Tolerant coercion of an agent card payload. Returns null when there is no
 * usable field at all — a partially decided card must survive. */
export function coerceProfileCard(raw: unknown): ProfileCard | null {
  if (!isDict(raw)) return null;
  const styleName = cleanStr(raw.style_name, NAME_MAX);
  const riskRaw = typeof raw.target_risk_level === 'string' ? raw.target_risk_level.trim() : null;
  const targetRisk =
    riskRaw !== null
      ? (RISK_LEVELS.find((r) => r.toLowerCase() === riskRaw.toLowerCase()) ?? null)
      : null;
  const knowledgeRaw = typeof raw.knowledge_level === 'string' ? raw.knowledge_level.trim() : null;
  const knowledge =
    knowledgeRaw !== null
      ? (KNOWLEDGE_LEVELS.find((k) => k === knowledgeRaw.toLowerCase()) ?? null)
      : null;
  const card: ProfileCard = {
    style_name: styleName,
    target_risk_level: targetRisk,
    turnover_tolerance: cleanStr(raw.turnover_tolerance, TURNOVER_MAX),
    style_description: cleanStr(raw.style_description, DESC_MAX),
    knowledge_level: knowledge,
  };
  const hasAny = Boolean(
    card.style_name ||
    card.target_risk_level ||
    card.turnover_tolerance ||
    card.style_description ||
    card.knowledge_level
  );
  return hasAny ? card : null;
}

/** Patch a card with user sheet edits: coerce the MERGED record so every
 * edit passes the same tolerance rules as a full snapshot (thesis
 * applyEdit twin). A null value clears its field. */
export function applyProfileEdit(
  card: ProfileCard,
  patch: Partial<ProfileCard>
): ProfileCard {
  if (!isDict(patch) || Object.keys(patch).length === 0) return card;
  const merged = { ...card, ...patch } as Record<string, unknown>;
  return coerceProfileCard(merged) ?? card;
}

/** Full session replay (spec §4.1 twin + 2026-10-31 §3.B): the winning
 * snapshot with `profile_edit` patches newer than it folded in
 * oldest→newest; `saved` iff a `profile_saved` receipt is newer than the
 * snapshot, `dismissed` iff a `profile_dismissed` receipt is (the card is
 * STILL returned for viewing, but the bar must not re-arm). A NEW snapshot
 * after either receipt re-arms it — same ordering rule as thesis.
 * Deliberate cross-side divergence, thesis-pair semantics: an edit newer
 * than a `saved` receipt still folds HERE (card returned with saved=true;
 * callers gate on the flag), while the backend replay_profile_card returns
 * None on that edge — exactly replaySession vs backend replay_card. */
export function replayProfileSession(messages: ChatMessage[]): {
  card: ProfileCard | null;
  saved: boolean;
  dismissed: boolean;
} {
  const edits: unknown[] = [];
  let saved = false;
  let dismissed = false;
  for (let i = messages.length - 1; i >= 0; i--) {
    const meta = messages[i]?.metadata;
    if (!isDict(meta)) continue;
    const edit = meta.profile_edit; // checked first: a meta with both is an edit
    if (isDict(edit)) {
      edits.push(edit);
      continue;
    }
    if (meta.profile_saved != null) {
      saved = true;
      continue;
    }
    if (meta.profile_dismissed != null) {
      dismissed = true;
      continue;
    }
    const card = coerceProfileCard(meta.profile_card);
    if (card !== null) {
      let folded = card;
      for (let j = edits.length - 1; j >= 0; j--) {
        folded = applyProfileEdit(folded, edits[j] as Partial<ProfileCard>);
      }
      return { card: folded, saved, dismissed };
    }
  }
  return { card: null, saved: false, dismissed: false };
}

/** One unsaved profile draft of the (kind, target) projection (T24). `pos` is
 * the winning snapshot's index in `messages` — the recency key consumers
 * order cross-kind entries by. */
export interface ProfileDraftEntry {
  card: ProfileCard;
  pos: number;
}

/** The profile slot of the entries machinery: at most ONE build_profile draft
 * per session (target is always null). `profile_edit` rows fold onto the
 * draft; a `profile_saved` OR `profile_dismissed` receipt newer than the
 * snapshot removes it; a NEW snapshot after either re-arms it (thesis
 * replayThesisEntries rules, single-slot). */
export function replayProfileEntries(messages: ChatMessage[]): ProfileDraftEntry[] {
  let draft: ProfileDraftEntry | null = null;
  for (let i = 0; i < messages.length; i++) {
    const meta = messages[i]?.metadata;
    if (!isDict(meta)) continue;
    const edit = meta.profile_edit;
    if (isDict(edit)) {
      if (draft) draft = { card: applyProfileEdit(draft.card, edit as Partial<ProfileCard>), pos: draft.pos };
      continue;
    }
    if (meta.profile_saved != null || meta.profile_dismissed != null) {
      draft = null; // receipt is newer than the draft seen so far
      continue;
    }
    const card = coerceProfileCard(meta.profile_card);
    if (card !== null) draft = { card, pos: i };
  }
  return draft !== null ? [draft] : [];
}

export function profileProgress(card: ProfileCard): { filled: number; total: number } {
  const filled =
    (card.style_name ? 1 : 0) +
    (card.target_risk_level != null ? 1 : 0) +
    (card.knowledge_level != null ? 1 : 0);
  return { filled, total: PROGRESS_FIELDS };
}

/** The save endpoint requires all three dimensions; the sheet's Save button
 * enables on exactly this. */
export function profileSaveEnabled(card: ProfileCard): boolean {
  return Boolean(card.style_name && card.target_risk_level != null && card.knowledge_level != null);
}
